"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SPECIALTIES, SPECIALTY_LABELS, type Specialty } from "@/types";

const DEBOUNCE_MS = 400;
const TIERS = ["1", "2", "3", "4"] as const;
const SERVICE_OPTIONS = [
  { value: "advisory", label: "Advisory" },
  { value: "ocio", label: "OCIO" },
  { value: "both", label: "Both models" },
] as const;

/** "any" is a sentinel — Radix Select items can't use an empty string value. */
const ANY = "any";

/**
 * Writes directory filters to URL search params (`router.replace`) so the
 * server-rendered directory page re-fetches with the new query — CONTRACTS
 * §5/§6.
 *
 * Deliberately NOT wrapped in Suspense: the directory page is
 * `force-dynamic`, so `useSearchParams()` never needs a prerender boundary
 * here, and the extra boundary shifted React's useId namespace between the
 * server and client renders — which surfaced as a hydration mismatch on the
 * Specialty dropdown trigger.
 */
export function FilterBar() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchParamsString = searchParams.toString();

  const applyParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParamsString);
      mutate(params);
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [pathname, router, searchParamsString]
  );

  const setParam = useCallback(
    (key: string, value: string) => {
      applyParams((params) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
    },
    [applyParams]
  );

  const toggleSpecialty = useCallback(
    (specialty: Specialty, checked: boolean) => {
      applyParams((params) => {
        const rest = params.getAll("specialty").filter((s) => s !== specialty);
        params.delete("specialty");
        const next = checked ? [...rest, specialty] : rest;
        next.forEach((s) => params.append("specialty", s));
      });
    },
    [applyParams]
  );

  const specialtyValues = searchParams.getAll("specialty");

  // Free-text search is the only debounced field: it's the one control
  // people type into continuously. Everything else is a discrete action
  // (select an option, check a box, type a 2-letter code) that can update
  // the URL immediately.
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  useEffect(() => {
    setQ(searchParams.get("q") ?? "");
  }, [searchParams]);
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = q.trim();
      if (trimmed !== (searchParams.get("q") ?? "")) setParam("q", trimmed);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const hasFilters = Array.from(searchParams.keys()).length > 0;

  return (
    <div className="flex flex-wrap items-end gap-4 rounded-card border border-border bg-card p-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="firm-search">Search</Label>
        <Input
          id="firm-search"
          placeholder="Firm name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-44"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="firm-tier">Tier</Label>
        <Select
          value={searchParams.get("tier") ?? ANY}
          onValueChange={(v) => setParam("tier", v === ANY ? "" : v)}
        >
          <SelectTrigger id="firm-tier" className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any tier</SelectItem>
            {TIERS.map((t) => (
              <SelectItem key={t} value={t}>
                Tier {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="firm-service">Service</Label>
        <Select
          value={searchParams.get("service") ?? ANY}
          onValueChange={(v) => setParam("service", v === ANY ? "" : v)}
        >
          <SelectTrigger id="firm-service" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any service</SelectItem>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            Specialty
            {specialtyValues.length > 0 ? ` (${specialtyValues.length})` : ""}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 p-2">
          {SPECIALTIES.map((specialty) => (
            <label
              key={specialty}
              className="flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none hover:bg-accent"
            >
              <Checkbox
                checked={specialtyValues.includes(specialty)}
                onCheckedChange={(checked) =>
                  toggleSpecialty(specialty, checked === true)
                }
              />
              {SPECIALTY_LABELS[specialty]}
            </label>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="firm-state">State</Label>
        <Input
          id="firm-state"
          placeholder="NY"
          maxLength={2}
          value={searchParams.get("state") ?? ""}
          onChange={(e) => setParam("state", e.target.value.toUpperCase())}
          className="w-16 uppercase"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="firm-min-aua">Min. AUA ($B)</Label>
        <Input
          id="firm-min-aua"
          type="number"
          min={0}
          placeholder="50"
          value={searchParams.get("minAua") ?? ""}
          onChange={(e) => setParam("minAua", e.target.value)}
          className="w-24"
        />
      </div>

      {hasFilters ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => router.replace(pathname, { scroll: false })}
        >
          Clear filters
        </Button>
      ) : null}
    </div>
  );
}
